import React, { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import DirectorPanelState from './DirectorPanelState';
import DirectorModal from './DirectorModal';
import { normalizeAppRole } from '../../config/appRoutes';
import { useMultiFetch } from '../../hooks/useMultiFetch';
import Button from '../ui/Button';
import { StatusPill } from '../ui/Status';
import DataTable from '../ui/DataTable';
import { useConfirm } from '../ui/ConfirmDialog';
import {
  DIRECTOR_DATA_UPDATED_EVENT,
  notifyDirectorDataUpdated,
  notifyDirectorLocationsUpdated
} from '../../utils/directorEvents';

const EMPTY_FORM = {
  code: '',
  name: '',
  primaryDirectorOwnerId: ''
};
const LOCATION_SETUP_STORAGE_KEY = 'manager-ops-location-setup-id';

function summarizeUsage(usage = {}) {
  const entries = [
    ['managers', usage.managerCount],
    ['goals', usage.goalCount],
    ['goal uploads', usage.uploadBatchCount],
    ['actuals', usage.actualCount],
    ['policies', usage.policyCount],
    ['attendance events', usage.attendanceEventCount],
    ['schedule events', usage.managerEventCount]
  ].filter(([, count]) => Number(count || 0) > 0);

  if (!entries.length) {
    return '';
  }

  return entries.map(([label, count]) => `${count} ${label}`).join(', ');
}

// Activation only ever requires the location's own essential
// information (code + name), which is already collected and
// required at creation — so any inactive-but-onboarding-tracked
// location is immediately activatable. Leader/owner, setup mode,
// first goals, and team acknowledgement are optional, later steps
// that never block activation; this just surfaces which of them are
// still outstanding as a helpful nudge, not a blocker.
function describeSetupGap(location) {
  if (location.isActive || !location.onboarding) {
    return '';
  }

  const outstanding = [];
  if (!location.primaryDirectorOwnerId) {
    outstanding.push('assign a Primary Director Owner');
  }
  if (!location.onboarding.setupMode) {
    outstanding.push('choose a setup mode');
  }
  if (!(Number(location.onboarding.goalCount || 0) > 0)) {
    outstanding.push('set first goals');
  }
  if (!(location.onboarding.completedSteps || []).includes('team')) {
    outstanding.push('acknowledge team setup');
  }

  if (!outstanding.length) {
    return 'Ready to activate.';
  }

  return `Ready to activate now. Optional next steps: ${outstanding.join(', ')}.`;
}

function formatPrimaryDirectorOwner(location) {
  const ownerName = location?.primaryDirectorOwner?.name;
  if (ownerName) {
    return ownerName;
  }

  return 'Unassigned';
}

function summarizeUserScope(user) {
  if (!user) {
    return '';
  }

  if (user.accessAllLocations) {
    return 'All locations';
  }

  const locationCodes = Array.from(new Set([
    user.locationCode || null,
    ...((user.assignedLocations || []).map((location) => location.locationCode || null))
  ].filter(Boolean)));

  return locationCodes.join(', ');
}

function DirectorLocationsPanel({ onNavigate }) {
  const confirm = useConfirm();
  const [locations, setLocations] = useState([]);
  const [directorManagers, setDirectorManagers] = useState([]);
  const [directorUsers, setDirectorUsers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [banner, setBanner] = useState('');
  const [error, setError] = useState('');
  const [deleteGuidance, setDeleteGuidance] = useState('');

  const { data: fetchedAdmin, isLoading, error: fetchError, refetch } = useMultiFetch({
    locations: '/api/locations',
    managers: '/api/managers?includeInactive=true',
    users: '/api/users',
  });

  useEffect(() => {
    if (fetchedAdmin.locations) setLocations(fetchedAdmin.locations);
    if (fetchedAdmin.managers) {
      setDirectorManagers(
        fetchedAdmin.managers
          .filter(manager => manager.managerRoleKey === 'director')
          .sort((a, b) => `${a.locationCode}${a.lastName}${a.firstName}`.localeCompare(`${b.locationCode}${b.lastName}${b.firstName}`))
      );
    }
    if (fetchedAdmin.users) {
      setDirectorUsers(fetchedAdmin.users.filter((user) => normalizeAppRole(user.role) === 'director'));
    }
    if (fetchError) {
      setError(fetchError);
    } else if (fetchedAdmin.locations) {
      setError('');
    }
  }, [fetchedAdmin, fetchError]);

  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
  }, [refetch]);

  const visibleLocations = useMemo(() => locations.filter(location => {
    if (filter === 'active') {
      return location.isActive;
    }
    if (filter === 'inactive') {
      return !location.isActive;
    }
    if (filter === 'setup') {
      return !location.isActive && location.onboarding;
    }
    return true;
  }), [filter, locations]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const closeModal = () => {
    resetForm();
    setIsModalOpen(false);
  };

  const openCreateModal = () => {
    resetForm();
    setBanner('');
    setError('');
    setDeleteGuidance('');
    setIsModalOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setBanner('');
    setError('');
    setDeleteGuidance('');

    try {
      const payload = {
        code: form.code,
        name: form.name,
        ...(editingId ? { primaryDirectorOwnerId: form.primaryDirectorOwnerId ? Number(form.primaryDirectorOwnerId) : null } : {})
      };

      const nextLocation = editingId
        ? await apiRequest(`/api/locations/${editingId}`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        })
        : await apiRequest('/api/locations', {
          method: 'POST',
          body: JSON.stringify(payload)
        });

      setLocations(current => {
        const otherRows = current.filter(location => location.id !== nextLocation.id);
        return [...otherRows, nextLocation].sort((a, b) => a.code.localeCompare(b.code));
      });
      setBanner(
        editingId
          ? `Updated location ${nextLocation.code}.`
          : `Added location ${nextLocation.code}. Complete starter setup before activation.`
      );
      notifyDirectorLocationsUpdated();
      notifyDirectorDataUpdated(`Director dashboard refreshed for ${nextLocation.code}.`);
      closeModal();
      if (!editingId && onNavigate) {
        try {
          window.sessionStorage.setItem(LOCATION_SETUP_STORAGE_KEY, String(nextLocation.id));
        } catch {
          // Setup still remains available from the Location Directory.
        }
        onNavigate('/location-setup');
      }
    } catch (requestError) {
      setError(requestError.message || 'Unable to save location.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (location) => {
    setEditingId(location.id);
    setForm({
      code: location.code,
      name: location.name,
      primaryDirectorOwnerId: location.primaryDirectorOwnerId ? String(location.primaryDirectorOwnerId) : ''
    });
    setBanner('');
    setError('');
    setDeleteGuidance('');
    setIsModalOpen(true);
  };

  const handleToggleActive = async (location) => {
    if (!location.isActive && location.onboarding) {
      try {
        window.sessionStorage.setItem(LOCATION_SETUP_STORAGE_KEY, String(location.id));
      } catch {
        // Ignore storage errors; setup page can still be opened.
      }
      onNavigate?.('/location-setup');
      return;
    }

    const confirmed = await confirm(
      location.isActive
        ? `Deactivate ${location.code}? Future operational use will stop, but historical records will remain attached to this location.`
        : `Activate ${location.code}? It will return to active location selection and future operational workflows.`,
      location.isActive
        ? { title: 'Deactivate location', confirmLabel: 'Deactivate', tone: 'danger' }
        : { title: 'Activate location', confirmLabel: 'Activate' }
    );

    if (!confirmed) {
      return;
    }

    setBanner('');
    setError('');
    setDeleteGuidance('');

    try {
      const updated = await apiRequest(`/api/locations/${location.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !location.isActive })
      });

      setLocations(current => current.map(item => (item.id === updated.id ? updated : item)));
      setBanner(`${updated.code} is now ${updated.isActive ? 'active' : 'inactive'}.`);
      notifyDirectorDataUpdated(`Director dashboard refreshed for ${updated.code}.`);
      notifyDirectorLocationsUpdated();
    } catch (requestError) {
      setError(requestError.message || 'Unable to update location state.');
    }
  };

  const handleDelete = async (location) => {
    const confirmed = await confirm(
      `Delete ${location.code}? Only unused locations can be deleted. If this location has operational history, deletion will fail and you should deactivate it instead.`,
      { title: 'Delete location', confirmLabel: 'Delete', tone: 'danger' }
    );

    if (!confirmed) {
      return;
    }

    setBanner('');
    setError('');
    setDeleteGuidance('');

    try {
      await apiRequest(`/api/locations/${location.id}`, { method: 'DELETE' });
      setLocations(current => current.filter(item => item.id !== location.id));
      if (editingId === location.id) {
        closeModal();
      }
      setBanner(`${location.code} deleted.`);
      notifyDirectorLocationsUpdated();
      notifyDirectorDataUpdated(`Director dashboard refreshed after deleting ${location.code}.`);
    } catch (requestError) {
      const usageSummary = summarizeUsage(requestError?.payload?.data?.usage);
      const recommendation = requestError?.payload?.data?.recommendation || '';
      setError(requestError.message || 'Unable to delete location.');
      setDeleteGuidance(
        usageSummary || recommendation
          ? [usageSummary ? `Delete blocked because this location still has ${usageSummary}.` : '', recommendation]
            .filter(Boolean)
            .join(' ')
          : ''
      );
    }
  };

  const ownerOptions = useMemo(
    () => {
      if (!editingId) {
        return [];
      }

      const userByManagerId = new Map(
        directorUsers
          .filter((user) => user.managerId)
          .map((user) => [Number(user.managerId), user])
      );

      return directorManagers
        .filter((manager) => {
          if (Number(manager.locationId) === Number(editingId)) {
            return true;
          }

          const linkedUser = userByManagerId.get(Number(manager.id));
          if (!linkedUser) {
            return false;
          }

          if (linkedUser.accessAllLocations) {
            return true;
          }

          return (linkedUser.assignedLocationIds || []).some((locationId) => Number(locationId) === Number(editingId));
        })
        .map((manager) => {
          const linkedUser = userByManagerId.get(Number(manager.id)) || null;
          return {
            ...manager,
            linkedUser,
            scopeSummary: linkedUser ? summarizeUserScope(linkedUser) : manager.locationCode
          };
        })
        .sort((a, b) => `${a.lastName}${a.firstName}${a.scopeSummary}`.localeCompare(`${b.lastName}${b.firstName}${b.scopeSummary}`));
    },
    [directorManagers, directorUsers, editingId]
  );

  return (
    <div className="director-admin-stack">
      <DirectorPanelState kind="loading" message={isLoading ? 'Loading location administration data...' : ''} />
      <DirectorPanelState kind="error" message={error} />
      <DirectorPanelState kind="success" message={!error ? banner : ''} />
      <DirectorPanelState kind="warning" message={!error ? '' : deleteGuidance} />

      <div className="director-split-grid">
        <section className="director-card">
          <div className="role-section-header">
            <h2>Location Actions</h2>
            <span>{locations.filter(location => location.isActive).length} active</span>
          </div>
          <p className="role-section-copy">
            Create and manage locations. Deactivate/reactivate is the normal lifecycle path.
          </p>
          <div className="director-form-actions">
            <Button onClick={openCreateModal}>
              Create Location
            </Button>
          </div>
        </section>

        <section className="director-card director-note-card">
          <div className="role-section-header">
            <h2>Delete Rules</h2>
            <span>{locations.filter(location => location.isActive).length} active</span>
          </div>
          <ul className="director-bullet-list">
            <li>Deactivate/reactivate is the normal workflow.</li>
            <li>Unused locations can be deleted.</li>
            <li>Used locations cannot be deleted.</li>
            <li>Inactive locations preserve history.</li>
          </ul>
        </section>
      </div>

      <section className="director-card">
        <div className="role-section-header">
          <h2>Location Directory</h2>
          <span>{visibleLocations.length} shown</span>
        </div>
        <div className="director-toolbar">
          <div className="director-toolbar-group">
            <label htmlFor="location-filter">Filter</label>
            <select id="location-filter" value={filter} onChange={(event) => setFilter(event.target.value)}>
              <option value="active">Active</option>
              <option value="setup">Setup Needed</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>
        <DataTable
          tableClassName="director-locations-table"
          emptyMessage="No locations are visible for the current scope."
          isEmpty={!visibleLocations.length}
          columns={[
            { label: 'Code', className: 'director-locations-col-code' },
            { label: 'Name' },
            { label: 'Primary Director Owner' },
            { label: 'Active', className: 'director-locations-col-status' },
            { label: 'Setup', className: 'director-locations-col-status' },
            { label: 'Actions', className: 'director-locations-col-actions' }
          ]}
        >
          {visibleLocations.map(location => (
            <tr key={location.id}>
              <td className="director-locations-col-code">{location.code}</td>
              <td>{location.name}</td>
              <td>{formatPrimaryDirectorOwner(location)}</td>
              <td className="director-locations-col-status">
                <StatusPill tone={location.isActive ? 'active' : 'inactive'}>
                  {location.isActive ? 'active' : 'inactive'}
                </StatusPill>
              </td>
              <td className="director-locations-col-status">
                <StatusPill
                  tone={location.onboardingStatus === 'Active' ? 'active' : 'inactive'}
                  title={describeSetupGap(location) || undefined}
                >
                  {location.onboardingStatus || (location.isActive ? 'Active' : 'Inactive')}
                </StatusPill>
              </td>
              <td className="director-locations-col-actions">
                <div className="director-table-actions">
                  {location.onboardingStatus !== 'Active' && location.onboarding ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        try {
                          window.sessionStorage.setItem(LOCATION_SETUP_STORAGE_KEY, String(location.id));
                        } catch {
                          // Ignore storage errors; setup page can still be opened.
                        }
                        onNavigate?.('/location-setup');
                      }}
                    >
                      Setup
                    </Button>
                  ) : null}
                  {location.isActive ? (
                    <Button size="sm" variant="secondary" onClick={() => onNavigate?.('/managers')}>
                      Add Leader
                    </Button>
                  ) : null}
                  <Button size="sm" variant="secondary" onClick={() => handleEdit(location)}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant={location.isActive ? 'danger' : 'secondary'}
                    onClick={() => handleToggleActive(location)}
                  >
                    {location.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => handleDelete(location)}>
                    Delete
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </DataTable>
      </section>

      {isModalOpen ? (
        <DirectorModal
          title={editingId ? 'Edit Location' : 'Create Location'}
          badge={editingId ? 'Update' : 'New'}
          onClose={closeModal}
        >
          <form className="director-form-grid" onSubmit={handleSubmit}>
            <label>
              Location Code
              <input value={form.code} onChange={(event) => setForm(current => ({ ...current, code: event.target.value.toUpperCase() }))} required />
            </label>
            <label>
              Location Name
              <input value={form.name} onChange={(event) => setForm(current => ({ ...current, name: event.target.value }))} required />
            </label>
            {editingId ? (
              <label className="director-form-span">
                Primary Director Owner
                <p className="director-muted">
                  Optional — assign after adding leaders. A location does not need an owner to be activated;
                  add leaders first, then come back here to designate one as this location&apos;s owner.
                </p>
                <select
                  value={form.primaryDirectorOwnerId}
                  onChange={(event) => setForm(current => ({ ...current, primaryDirectorOwnerId: event.target.value }))}
                  disabled={!ownerOptions.length}
                >
                  <option value="">Unassigned</option>
                  {ownerOptions.map(manager => (
                    <option key={manager.id} value={manager.id}>
                      {manager.firstName} {manager.lastName} ({manager.scopeSummary || manager.locationCode || 'No scope'})
                    </option>
                  ))}
                </select>
                {!ownerOptions.length ? (
                  <p className="director-muted">
                    No eligible Directors are scoped to this location yet. Add a leader with the Director role first.
                  </p>
                ) : null}
              </label>
            ) : null}
            <div className="director-form-actions director-form-actions-spread director-form-span">
              <Button type="submit" loading={isSubmitting} loadingLabel="Saving…">
                {editingId ? 'Save Location' : 'Add Location'}
              </Button>
              <Button size="sm" variant="secondary" onClick={closeModal}>
                Cancel
              </Button>
            </div>
          </form>
        </DirectorModal>
      ) : null}
    </div>
  );
}

export default DirectorLocationsPanel;
